import { useEffect, useState } from "react";
import { useForm } from "@tanstack/react-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryKeys";
import { Field, Input, Select, Textarea } from "./Field";
import { Button } from "./Button";
import { TagChipInput, mergePendingTag } from "./TagChipInput";

// T21: accent swatch palette. Hex values match the project-card colours used
// elsewhere in the demo data (see designs/Projects.html); the design's
// settings swatch row uses these exact tokens.
const PROJECT_COLORS = [
  "#1f8a5b", // green
  "#e8612c", // orange
  "#3b82d4", // blue
  "#8b5cf6", // violet
  "#d98314", // amber
  "#db2777", // pink
  "#0ea5a5", // teal
  "#94a0b4", // muted
] as const;

const DEFAULT_PROJECT_COLOR = PROJECT_COLORS[0];

export type ProjectFormValues = {
  name: string;
  kind: "standing" | "initiative";
  color: string;
  description: string;
  /** Date-only string (YYYY-MM-DD), or "" when unset. */
  targetDate: string;
  status: "active" | "on_hold" | "archived";
  /** T46: tag names as chips. Edit mode only — create POST has no tags. */
  tagNames: string[];
};

export type ProjectFormProps = {
  onCancel?: () => void;
  /** When set, the form renders in edit mode: fields are prefilled, a status
   * select appears, and submission goes through `onSubmit` instead of the
   * built-in create-project mutation. */
  initial?: Partial<ProjectFormValues>;
  onSubmit?: (values: ProjectFormValues) => Promise<unknown>;
  submitLabel?: string;
};

export function ProjectForm({
  onCancel,
  initial,
  onSubmit,
  submitLabel = "Create project",
}: ProjectFormProps = {}) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const isEdit = initial !== undefined;
  // Chip-input scratch text (same commit-on-comma/Enter pattern as TaskForm).
  const [tagDraft, setTagDraft] = useState("");
  // Edit mode has no navigation on save, so without an explicit confirmation a
  // successful save just flips the button back to its label — users read that
  // as "nothing happened / my changes didn't save". `savedTick` increments on
  // each successful edit-save and drives a brief "Saved ✓" flash.
  const [savedTick, setSavedTick] = useState(0);
  const [showSaved, setShowSaved] = useState(false);
  useEffect(() => {
    if (savedTick === 0) return;
    setShowSaved(true);
    const t = setTimeout(() => setShowSaved(false), 2500);
    return () => clearTimeout(t);
  }, [savedTick]);
  const mut = useMutation({
    mutationFn: async (body: ProjectFormValues) => {
      // Flush any text still in the chip input so "blocker" typed without a
      // trailing comma isn't silently dropped on save.
      body = { ...body, tagNames: mergePendingTag(body.tagNames, tagDraft) };
      if (onSubmit) return onSubmit(body);
      return api.createProject({
        name: body.name,
        kind: body.kind,
        color: body.color,
        description: body.description || undefined,
        targetDate: body.targetDate || undefined,
      });
    },
    onSuccess: (d) => {
      if (onSubmit) {
        // Edit mode: caller owns invalidation + close. Flash the confirmation.
        setSavedTick((n) => n + 1);
        return;
      }
      qc.invalidateQueries({ queryKey: queryKeys.projects() });
      const created = d as { project: { id: string } };
      navigate({
        to: "/projects/$projectId",
        params: { projectId: created.project.id },
      });
    },
  });
  const form = useForm({
    defaultValues: {
      name: initial?.name ?? "",
      kind: initial?.kind ?? ("initiative" as "standing" | "initiative"),
      color: initial?.color ?? (DEFAULT_PROJECT_COLOR as string),
      description: initial?.description ?? "",
      targetDate: initial?.targetDate ?? "",
      status: initial?.status ?? ("active" as ProjectFormValues["status"]),
      tagNames: initial?.tagNames ?? ([] as string[]),
    },
    onSubmit: async ({ value }) => mut.mutateAsync(value),
  });
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        form.handleSubmit();
      }}
      style={{ display: "grid", gap: 12 }}
    >
      <form.Field
        name="name"
        validators={{
          onChange: ({ value }) => (!value ? "Name required" : undefined),
        }}
      >
        {(field) => (
          <Field
            label="Project name"
            id="project-name"
            error={field.state.meta.errors[0] ?? undefined}
          >
            <Input
              id="project-name"
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              placeholder="e.g. City Charity Run 2026"
              required
              autoFocus
            />
          </Field>
        )}
      </form.Field>
      <form.Field name="description">
        {(field) => (
          <Field label="Description" id="project-description">
            <Textarea
              id="project-description"
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              placeholder="What is this project about? (optional)"
              rows={3}
            />
          </Field>
        )}
      </form.Field>
      <form.Field name="kind">
        {(field) => (
          <Field label="Kind" id="project-kind">
            <Select
              id="project-kind"
              value={field.state.value}
              onChange={(e) =>
                field.handleChange(e.target.value as "standing" | "initiative")
              }
            >
              <option value="initiative">Initiative (has end date)</option>
              <option value="standing">Standing (ongoing)</option>
            </Select>
          </Field>
        )}
      </form.Field>
      <form.Field name="targetDate">
        {(field) => (
          <Field label="Target date" id="project-target-date">
            <Input
              id="project-target-date"
              type="date"
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
            />
          </Field>
        )}
      </form.Field>
      {isEdit ? (
        <form.Field name="tagNames">
          {(field) => (
            <Field label="Tags" id="project-tags">
              <TagChipInput
                id="project-tags"
                names={field.state.value}
                onNamesChange={field.handleChange}
                draft={tagDraft}
                onDraftChange={setTagDraft}
              />
            </Field>
          )}
        </form.Field>
      ) : null}
      {isEdit ? (
        <form.Field name="status">
          {(field) => (
            <Field label="Status" id="project-status">
              <Select
                id="project-status"
                value={field.state.value}
                onChange={(e) =>
                  field.handleChange(
                    e.target.value as ProjectFormValues["status"],
                  )
                }
              >
                <option value="active">Active</option>
                <option value="on_hold">On hold</option>
                <option value="archived">Archived</option>
              </Select>
            </Field>
          )}
        </form.Field>
      ) : null}
      <form.Field name="color">
        {(field) => (
          <Field label="Color" id="project-color">
            <div
              className="swatch-pick"
              role="radiogroup"
              aria-label="Project color"
              id="project-color"
            >
              {PROJECT_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  role="radio"
                  aria-checked={field.state.value === c}
                  aria-label={`Color ${c}`}
                  className={`sw${field.state.value === c ? " on" : ""}`}
                  style={{ background: c }}
                  onClick={() => field.handleChange(c)}
                />
              ))}
            </div>
          </Field>
        )}
      </form.Field>
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: 12,
        }}
      >
        {showSaved && (
          <span
            role="status"
            className="t-sm"
            style={{ color: "var(--green-600)" }}
          >
            Saved ✓
          </span>
        )}
        {onCancel && (
          <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button
          type="submit"
          variant="primary"
          size="sm"
          disabled={mut.isPending}
        >
          {mut.isPending ? "Saving…" : submitLabel}
        </Button>
      </div>
      {mut.isError && (
        <p
          role="alert"
          className="t-sm"
          style={{ color: "var(--color-danger)" }}
        >
          Couldn&apos;t save. Try again.
        </p>
      )}
    </form>
  );
}
